/* eslint-disable no-underscore-dangle */
import * as d3 from 'd3';

import type { CourseNodeBase, MapMargin, NodeEntity } from './coursemap.types';

// ── React-friendly text utilities ────────────────────────────────────────────
// These are counterparts to the D3 text-wrap helpers below. React-created SVG
// elements have no D3 datum attached, so the originals would fail with
// "Cannot read properties of undefined (reading 'data')". These receive node
// metadata as explicit arguments instead.

/**
 * Wraps the text content of an SVGTextElement into multiple <tspan> lines so
 * that no line exceeds `width` pixels.
 */
export function wrapTextElement(el: SVGTextElement, width: number): void {
	const sel = d3.select(el);
	const words = (el.textContent ?? '').split(/\s+/).filter(Boolean);
	const x = sel.attr('x');
	const y = sel.attr('y');
	const lineHeight = 1.3;
	let lineNumber = 0;
	let line: string[] = [];

	let tspan = sel.text(null).append('tspan').attr('x', x).attr('y', y).attr('dy', '0em');

	for (const word of words) {
		line.push(word);
		tspan.text(line.join(' '));
		if ((tspan.node()?.getComputedTextLength() ?? 0) > width) {
			line.pop();
			tspan.text(line.join(' '));
			line = [word];
			lineNumber += lineHeight;
			tspan = sel
				.append('tspan')
				.attr('x', x)
				.attr('y', y)
				.attr('dy', `${lineNumber}em`)
				.text(word);
		}
	}
}

/**
 * Vertically centres wrapped text for discipline and root (depth-0) nodes.
 */
export function adjustDisciplineTextPosition(
	el: SVGTextElement,
	height: number,
	isDiscipline: boolean,
	depth: number
): void {
	const sel = d3.select(el);
	sel.attr('dominant-baseline', 'middle');

	if (isDiscipline || depth === 0) {
		const childCount = el.childElementCount;
		const offset = childCount < 2 ? -14 : 0;
		const textHeight = el.getClientRects()[0]?.height ?? 0;
		const y = (height / 2 + textHeight / 2) / childCount;
		sel.attr('y', String(y + offset));
		sel.selectAll('tspan').attr('y', String(y + offset));
	}
}
// ─────────────────────────────────────────────────────────────────────────────

export const rectNode = { width: 200, height: 110, extWidth: 250 };

/** Layout-only nodes (e.g. "- Spacer -"); dashed cards on the map; excluded from map search only. */
export const SPACER_COURSE_NAME = '- Spacer -';

export function isSpacerMapNode(name: string | undefined): boolean {
	return Boolean(name?.includes('Spacer'));
}

// Collapse the node and all it's children
export function collapseTree(d: NodeEntity) {
	if (d.children) {
		d._children = d.children;
		d._children.forEach(collapseTree);
		d.children = null;
	}
}

/**
 * Every D3 hierarchy node under `root`, including branches stored in `_children`
 * while collapsed. `d3.Node#descendants()` only follows `children`, so map search
 * needs this walk to index courses before the user expands a discipline.
 */
export function collectAllHierarchyNodes(root: NodeEntity): NodeEntity[] {
	const out: NodeEntity[] = [];
	function walk(n: NodeEntity) {
		out.push(n);
		const next = n.children ?? n._children;
		if (next) for (const c of next) walk(c);
	}
	walk(root);
	return out;
}

// Expand the node and all it's children
export function expandTree(d: NodeEntity) {
	const children = d.children ? d.children : d._children;
	if (d._children) {
		d.children = d._children;
		d._children = null;
	}
	if (children) {
		children.forEach((n: NodeEntity) => {
			expandTree(n);
		});
	}
}

export function click(d: NodeEntity, callback: (d: NodeEntity) => void, isAdmin: boolean) {
	if (isAdmin) {
		return;
	}
	if (d.children) {
		d._children = d.children;
		d.children = null;
	} else {
		d.children = d._children;
		d._children = null;
		expandTree(d);
	}
	callback(d);
}

/**
 * Creates the pan limits for the map based on the number of node children, margins, client width, and admin status.
 *
 * @param length - The number of node children.
 * @param margin - The map margin configuration.
 * @param clientWidth - The width of the client viewport.
 * @param isAdmin - The flag indicating whether the user is an admin.
 * @returns The pan limits as an array of coordinate bounds.
 */
export function createPanLimits(
	length: number,
	margin: MapMargin,
	clientWidth: number,
	isAdmin: boolean
) {
	const nodeChildren = length;
	const margins = margin.left + margin.right;
	const xMaxPan = nodeChildren * clientWidth + margins;
	const yMaxPan = (nodeChildren / 2) * clientWidth - margins;

	const noBounds = [
		[Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY],
		[Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY],
	] as [[number, number], [number, number]];

	const limitedBounds = [
		[-clientWidth, -yMaxPan],
		[xMaxPan, yMaxPan],
	] as [[number, number], [number, number]];

	return isAdmin ? noBounds : limitedBounds;
}

interface RootCoords {
	name: string;
	x: number;
	y: number;
	parent: any;
}
/**
 * Creates a curved (diagonal) path from parent to the child nodes in a tree layout.
 *
 * @param s - The source node coordinates.
 * @param d - The destination node coordinates.
 * @returns The SVG path string for the diagonal connection.
 */
export function diagonal(s: RootCoords | NodeEntity, d: RootCoords | NodeEntity) {
	let path = `M ${s.y} ${s.x} C ${(s.y + d.y) / 2} ${s.x},${s.y + 10} ${d.x},${d.y} ${d.x}`;

	const name = (s as NodeEntity).data?.name ?? (s as RootCoords).name;
	if (name.includes('Spacer')) {
		path = `M ${s.y + 0} ${s.x} L ${s.y} ${s.x} C ${(s.y + d.y) / 2} ${s.x},${s.y + 10} ${
			d.x
		},${d.y} ${d.x}`;

		if (
			(d as NodeEntity).data.name.includes('Spacer') &&
			(s as NodeEntity).data.children?.length === 0
		) {
			path = `M ${s.y + rectNode.width} ${s.x} L ${s.y} ${s.x} C ${(s.y + d.y) / 2} ${s.x},${
				s.y + 10
			} ${d.x},${d.y} ${d.x}`;
		}
	}

	return path;
}

// ? add for straight lines replace('C', 'L');
/**
 * Creates the SVG path string for drawing connections between course map nodes.
 *
 * @param d - The data object representing a node.
 * @returns The SVG path string for the connection.
 */
export const createCourseMapPaths = (d: any) => {
	if (d.name.includes('Spacer') && d.parent.y !== d.y) {
		return `M${d.y},${d.x}C${(d.y + d.parent.y) / 2},
		${d.x} ${(d.y + d.parent.y) / 2 - rectNode.extWidth + 50},
		${d.parent.x} ${d.parent.y - 150},${d.parent.x}`;
	}
	if (d.parent.y === d.y) {
		return `M${d.y - rectNode.width / 2},${d.x} L${d.parent.y - rectNode.width / 2},${d.parent.x}`;
	}
	return `M${d.y},${d.x}C${(d.y + d.parent.y) / 2},
		${d.x} ${(d.y + d.parent.y) / 2 - rectNode.extWidth},
		${d.parent.x} ${d.parent.y},${d.parent.x}`;
};

export function isEmpty(obj: CourseNodeBase | Record<string, never>) {
	return Object.keys(obj).length === 0;
}

/** True if a map node uses this course id as `tracker` / `id` (course appears on the map). */
export function courseIdIsOnMap(root: CourseNodeBase, courseId: string): boolean {
	if (!courseId) return false;
	if (root.tracker === courseId || root.id === courseId) return true;
	for (const child of root.children ?? []) {
		if (courseIdIsOnMap(child, courseId)) return true;
	}
	return false;
}

// ── Merge filtering ───────────────────────────────────────────────────────────
/**
 * Given a D3 hierarchy node, returns the list of nodes that are valid merge
 * targets for that node.
 *
 * Rules:
 *  - Must be in the same discipline.
 *  - Must be at the same depth or one level above the selected node.
 *  - Must not be the selected node itself (matched by tracker).
 *  - Must not be the selected node's current parent (matched by D3 id).
 *  - Ancestors that qualify must have ≥ 2 children (a meaningful branch point).
 *  - Results are deduplicated by D3 id and sorted alphabetically by name.
 */
export function getMergeOptions(selectedNode: any): any[] {
	const sn = selectedNode;
	if (!sn) return [];

	const hasMultiChildren = (sn.ancestors() as any[]).filter(
		(a: any) => a.children?.length >= 2 && a.data.discipline === sn.data.discipline && a.depth >= 1
	);

	const theList = hasMultiChildren
		.flatMap((a: any) => a.descendants() as any[])
		.filter(
			(a: any) =>
				a.data.discipline === sn.data.discipline &&
				(a.depth === sn.depth || a.depth === sn.depth - 1) &&
				a.data.tracker !== sn.parent?.data?.tracker &&
				a.data.tracker !== sn.data.tracker
		)
		.sort((a: any, b: any) => (a.data.name < b.data.name ? -1 : Number(a.data.name > b.data.name)));

	return Array.from(new Set(theList.map((a: any) => a.data.tracker))).map((tracker) =>
		theList.find((a: any) => a.data.tracker === tracker)
	) as any[];
}
// ─────────────────────────────────────────────────────────────────────────────
